import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { stripImageData } from "../helpers/helpers";

// Redux actions
import { update as updateError } from "../store/slices/error";
import { update as updateShowSpinner } from "../store/slices/showSpinner";
import { update as updateSubmitted } from "../store/slices/showSpinner";

// components
import Button from "../components/Button";
import PageWrap from "../components/PageWrap";
import HalfHeight from "../components/HalfHeight";
import SkeletonBox from "../components/SkeletonBox";

import routes from "../routes/routes";

import Gallery from "../components/Gallery";

// import Gallery from "../Gallery";

import api from "../api/api";

const StyledPageWrap = styled(PageWrap)`
  padding-left: 0;
  padding-right: 0;
`;

const StyledDiv = styled.div`
  padding: 2em 1em 0;
`;

function ConfirmPicture() {
  // Redux values
  const pictures = useSelector((state) => state.gallery.value.pictures);
  const country = useSelector((state) => state.country.value);
  const documentType = useSelector((state) => state.documentType.value);
  const token = useSelector((state) => state.token.value);
  const guid = useSelector((state) => state.guid.value);
  const scanUser = useSelector((state) => state.scanUser.value);
  const submitted = useSelector((state) => state.submitted.value);

  const dispatch = useDispatch();
  const history = useHistory();

  /**
   * generate list of images to show
   */
  const documentList = () => {
    const documents = [];
    if (documentType === "drivers-license") {
      // first picture will be front of driver's license
      const frontPicture = pictures.find(
        (p) => p.documentCaptured === "driver's license front",
      );
      if (frontPicture) {
        documents.push(frontPicture);
      }
      // if usa/canada display barcode capture
      let backPicture;
      if (country === "usa-or-canada") {
        backPicture = pictures.find((p) => p.docType === "PDF417_BARCODE");
      }
      // if other country return the non-barcode picture
      if (country === "other-country") {
        backPicture = pictures.find(
          (p) =>
            p.docType === "DL_FRONT" &&
            p.documentCaptured === "driver's license back",
        );
      }
      if (backPicture) {
        documents.push(backPicture);
      }
    }
    if (documentType === "passport") {
      const picture = pictures.find((p) => p.documentCaptured === "passport");
      if (picture) {
        documents.push(picture);
      }
    }
    return documents;
  };

  // return true if ID was expired
  const checkIdExpired = (returnedData) => {
    // console.log("returned data: ", returnedData);
    if (
      returnedData.message &&
      typeof returnedData.message === "string" &&
      returnedData.message.toLowerCase() === "expired id"
    ) {
      return true;
    }
    return false;
  };

  /**
   * User accepts picture, send them back to next picture if needed
   * otherwise send them to the final confirmation screen
   */
  const acceptPictures = () => {
    if (!submitted) {
      dispatch(updateSubmitted(true));
      // console.log("accept pictures event", e);
      // tell OLA that user is done scanning id for application
      const stopScan = () =>
        api().olaStopScan({ token: token, guid: guid, scanUser: scanUser });

      // turn on loading state
      dispatch(updateShowSpinner(true));

      if (documentType === "drivers-license") {
        const driversLicenseFront = documentList().find(
          (p) => p.documentCaptured === "driver's license front",
        );
        const driversLicenseBack = documentList().find(
          (p) => p.documentCaptured === "driver's license back",
        );
        /**
         * if not usa/canada or manually captured back of ID
         * submit as pictures
         */
        if (
          country === "other-country" ||
          (driversLicenseBack &&
            driversLicenseBack.modeUsed === "MANUAL_CAPTURE")
        ) {
          const payload = {
            token: token,
            scanUser: scanUser,
            frontimage: stripImageData(driversLicenseFront.image),
            backimage: stripImageData(driversLicenseBack.image),
          };
          // console.log("payload", payload);
          api()
            .backImageIdCheck(payload)
            .then((result) => {
              // console.log(result.data);
              dispatch(updateShowSpinner(false));
              if (checkIdExpired(result.data)) {
                dispatch(updateError({ type: "expired id", event: "api" }));
                history.push(routes.error);
              } else {
                // window.alert(JSON.stringify(result.data));
                stopScan()
                  .then(() => {
                    history.push(routes.thankYou);
                  })
                  .catch((error) => {
                    // console.log(error);
                    dispatch(updateShowSpinner(false));
                    dispatch(updateError({ type: "api", event: "stop scan" }));
                    history.push(routes.error);
                  });
              }
            })
            .catch((error) => {
              // console.log(error);
              dispatch(updateError({ type: "api", event: "id check" }));
              dispatch(updateShowSpinner(false));
              history.push(routes.error);
            });
        } else {
          /**
           * if USA/Canada and auto capture submit back of ID as barcode data
           */
          const payload = {
            token: token,
            scanUser: scanUser,
            frontimage: stripImageData(driversLicenseFront.image),
            backEncodedData: { PDF417: driversLicenseBack.decodedStr },
          };
          // console.log("payload", payload);
          api()
            .backEncodedIdCheck(payload)
            .then((result) => {
              // console.log(result.data);
              // window.alert(JSON.stringify(result.data));
              dispatch(updateShowSpinner(false));
              if (checkIdExpired(result.data)) {
                dispatch(updateError({ type: "expired id", event: "api" }));
                history.push(routes.error);
              } else {
                return stopScan()
                  .then(() => {
                    history.push(routes.thankYou);
                  })
                  .catch((error) => {
                    dispatch(updateShowSpinner(false));
                    dispatch(updateError({ type: "api", event: "stop scan" }));
                    history.push(routes.error);
                  });
              }
            })
            .catch((error) => {
              // console.log(error);
              dispatch(updateError({ type: "api", event: "id check" }));
              dispatch(updateShowSpinner(false));
              history.push(routes.error);
            });
        }
      }
      /**
       * submit passport image for processing
       */
      if (documentType === "passport") {
        const passportImage = documentList().find(
          (p) => p.documentCaptured === "passport",
        );
        const payload = {
          token: token,
          scanUser: scanUser,
          image: stripImageData(passportImage.image),
        };
        // console.log("payload", payload);
        api()
          .passportCheck(payload)
          .then((result) => {
            // console.log(result.data);
            // window.alert(JSON.stringify(result.data));
            dispatch(updateShowSpinner(false));
            if (checkIdExpired(result.data)) {
              dispatch(updateError({ type: "expired id", event: "api" }));
              history.push(routes.error);
            } else {
              return stopScan()
                .then(() => {
                  history.push(routes.thankYou);
                })
                .catch((error) => {
                  // console.log(error);
                  dispatch(updateError({ type: "api", event: "stop scan" }));
                  dispatch(updateShowSpinner(false));
                  history.push(routes.error);
                });
            }
          })
          .catch((error) => {
            // console.log(error);
            dispatch(updateError({ type: "api", event: "id check" }));
            dispatch(updateShowSpinner(false));
            history.push(routes.error);
          });
      }
    }
  };

  // return various text / jsx for page that's context dependent
  const step = () => {
    if (documentType === "drivers-license") {
      return "3 of 3";
    }
    if (documentType === "passport") {
      return "2 of 2";
    }
  };

  return (
    <StyledPageWrap noHeader>
      <HalfHeight dark>
        <Gallery documentList={documentList} />
      </HalfHeight>
      <HalfHeight light>
        <StyledDiv>
          <SkeletonBox>
            <strong>Step {step()}</strong>
          </SkeletonBox>
          <SkeletonBox>
            <h2>Review Your Photos</h2>
          </SkeletonBox>
          <SkeletonBox>
            <p>
              Your document was captured successfully.
              <br />
              <br />
              Submit your document?
            </p>
          </SkeletonBox>
        </StyledDiv>
        <StyledDiv>
          <Button
            type="button"
            onClick={() => acceptPictures()}
            disabled={submitted}
          >
            Submit
          </Button>
        </StyledDiv>
      </HalfHeight>
    </StyledPageWrap>
  );
}

export default ConfirmPicture;
